import type React from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'

const NAVIGATION_SECTIONS = [
  { id: 'what-is-pelgo', label: 'What is Pelgo' },
  { id: 'how-it-works', label: 'How it Works' },
  { id: 'faq', label: 'FAQ' },
  { id: 'pricing', label: 'Pricing' },
] as const

type MarketingNavProps = {
  /** Landing page with transparent header that changes on scroll and in-page smooth scrolling. */
  variant: 'landing'
  /** Whether the header should render as scrolled (used for landing). */
  isScrolled: boolean
  /** In-page scroll handler used only on landing. */
  onScrollToSection: (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => void
}

type StaticMarketingNavProps = {
  /** Standalone pages (contact, terms, privacy, etc.) with solid background header. */
  variant: 'standalone'
}

type Props = MarketingNavProps | StaticMarketingNavProps

export function MarketingNav(props: Props) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const isLanding = props.variant === 'landing'
  const isScrolled = isLanding ? props.isScrolled : true

  const headerClassName = isLanding
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/80 backdrop-blur-md border-b border-gray-100' : 'bg-transparent'
      }`
    : 'fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 transition-all duration-300'

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    if (isLanding) {
      props.onScrollToSection(e, sectionId)
      setIsMobileMenuOpen(false)
    } else {
      // Standalone pages: navigate to landing with hash; allow router navigation to handle it.
      setIsMobileMenuOpen(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const logoSrc = isLanding
    ? isScrolled
      ? '/logo-black.png'
      : '/logo-white.png'
    : '/logo-black.png'
  const desktopLinkClassesLanding = isScrolled
    ? 'px-5 py-2 rounded-full text-sm font-medium transition-colors text-gray-900 hover:bg-gray-100'
    : 'px-5 py-2 rounded-full text-sm font-medium transition-colors text-white hover:bg-white/10'

  const desktopLinkClassesStatic =
    'px-5 py-2 rounded-full text-sm font-medium transition-colors text-gray-900 hover:bg-gray-100'

  const desktopLinkClasses = isLanding ? desktopLinkClassesLanding : desktopLinkClassesStatic

  const mobileIconColor = isLanding
    ? isScrolled
      ? 'text-gray-900'
      : 'text-white'
    : 'text-gray-900'

  return (
    <>
      <header className={headerClassName}>
        <div className="container mx-auto px-4 md:px-16 py-4 md:py-8">
          <nav className="flex items-center justify-between">
            <Link
              to="/"
              className="flex-shrink-0"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            >
              <img
                src={logoSrc}
                alt="Pelgo"
                className="h-8 md:h-10 w-auto transition-opacity duration-300"
                style={{ objectFit: 'contain' }}
              />
            </Link>
            <div className="hidden lg:flex items-center gap-2">
              {NAVIGATION_SECTIONS.map((section) => (
                <Link
                  key={section.id}
                  to={isLanding ? `#${section.id}` : `/#${section.id}`}
                  className={desktopLinkClasses}
                  onClick={(e) => handleNavClick(e, section.id)}
                >
                  {section.label}
                </Link>
              ))}
              <Link
                to="/news"
                className={desktopLinkClasses}
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              >
                News
              </Link>
              <Link
                to="/contact"
                className={desktopLinkClasses}
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              >
                Contact
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <Button
                asChild
                variant="outline"
                className={`hidden lg:flex rounded-full px-6 py-3 text-sm font-semibold min-h-[48px] bg-transparent ${
                  isLanding && !isScrolled
                    ? 'border-white text-white hover:bg-white/10 hover:text-white'
                    : 'border-gray-900 text-gray-900 hover:bg-gray-100'
                }`}
              >
                <a href="https://embrace.pelgo.com" target="_blank" rel="noopener noreferrer">
                  Log In
                </a>
              </Button>
              <Button
                asChild
                className="hidden lg:flex bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-sm font-semibold min-h-[48px]"
              >
                <Link to="/contact" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Request Free Trial
                </Link>
              </Button>
              {/* Mobile: Request Free Trial button, Log In button, and hamburger icon */}
              <div className="flex items-center gap-2 lg:hidden">
                <Button
                  asChild
                  className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-4 py-2 text-xs font-semibold min-h-[40px] whitespace-nowrap"
                >
                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Request Free Trial
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className={`rounded-full px-4 py-2 text-xs font-semibold min-h-[40px] whitespace-nowrap bg-transparent ${
                    isLanding && !isScrolled
                      ? 'border-white text-white hover:bg-white/10 hover:text-white'
                      : 'border-gray-900 text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <a href="https://embrace.pelgo.com" target="_blank" rel="noopener noreferrer">
                    Log In
                  </a>
                </Button>
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className={`p-2 transition-colors ${mobileIconColor}`}
                  aria-label="Toggle menu"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    role="img"
                    aria-label="Menu icon"
                  >
                    <title>Menu icon</title>
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Slide-Out Drawer */}
      {/* Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsMobileMenuOpen(false)
            }
          }}
          aria-hidden="true"
        />
      )}
      {/* Slide-Out Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white z-50 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Close button at top */}
          <div className="flex justify-end p-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-gray-900 transition-colors hover:bg-gray-100 rounded-full"
              aria-label="Close menu"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                role="img"
                aria-label="Close icon"
              >
                <title>Close icon</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex flex-col gap-2">
              {NAVIGATION_SECTIONS.map((section) => (
                <Link
                  key={section.id}
                  to={isLanding ? `#${section.id}` : `/#${section.id}`}
                  className="px-4 py-3 rounded-full text-sm font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                  onClick={(e) => handleNavClick(e, section.id)}
                >
                  {section.label}
                </Link>
              ))}
              <Link
                to="/news"
                className="px-4 py-3 rounded-full text-sm font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              >
                News
              </Link>
              <Link
                to="/contact"
                className="px-4 py-3 rounded-full text-sm font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              >
                Contact
              </Link>
              <a
                href="https://embrace.pelgo.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 rounded-full text-sm font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Log In
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
